import React from 'react';
import { Submission } from 'types/evaluation';
import { refreshAzureBlobUrl } from 'utils/azure';

interface PresentationTabProps {
  submission: Submission;
  submissionId: string;
  loadingStates: Record<string, boolean>;
  handleMediaLoad: (submissionId: string, mediaType: string) => void;
}

const PresentationTab: React.FC<PresentationTabProps> = ({
  submission,
  submissionId,
  loadingStates,
  handleMediaLoad,
}) => {
  return (
    <div>
      <h3 className="text-xl font-baijamjuree font-extrabold text-black mb-6">
        Presentation File
      </h3>

      {/* Fast Loading Options */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
        {/* Quick Actions Header */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h4 className="text-sm font-baijamjuree font-semibold text-blue-900 mb-1">
                📄 Presentation Viewer
              </h4>
              <p className="text-xs text-blue-700 font-baijamjuree">
                Choose your preferred viewing method for optimal performance
              </p>
            </div>

            {/* Quick Action Buttons */}
            <div className="flex flex-wrap gap-2">
              <a
                href={refreshAzureBlobUrl(submission.award_material)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-baijamjuree font-medium hover:bg-blue-700 transition-colors"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                Download
              </a>

              <a
                href={refreshAzureBlobUrl(submission.award_material)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-baijamjuree font-medium hover:bg-green-700 transition-colors"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                  />
                </svg>
                Open in New Tab
              </a>
            </div>
          </div>
        </div>

        {/* Embedded Viewer with Fallback */}
        <div className="relative">
          {/* Loading Overlay */}
          {loadingStates[`${submissionId}_presentation`] && (
            <div className="absolute inset-0 bg-gray-50 flex items-center justify-center z-10">
              <div className="text-center">
                <div className="animate-spin h-8 w-8 border-3 border-blue-500 border-t-transparent rounded-full mx-auto mb-3"></div>
                <p className="text-gray-600 font-baijamjuree font-medium text-sm">
                  Loading presentation...
                </p>
                <p className="text-gray-500 font-baijamjuree text-xs mt-1">
                  This may take a moment
                </p>
              </div>
            </div>
          )}

          {/* Smart Viewer - Detects file type */}
          <div className="bg-gray-100 rounded-lg overflow-hidden">
            {(() => {
              const fileUrl = refreshAzureBlobUrl(submission.award_material);
              const fileName = fileUrl.split('/').pop().toLowerCase();
              const isPDF = fileName.includes('.pdf');

              if (isPDF) {
                // Use browser's native PDF viewer for PDF files
                return (
                  <iframe
                    src={fileUrl}
                    className="w-full h-[50vh] min-h-[400px] max-h-[600px] border-0"
                    title="PDF Document"
                    frameBorder="0"
                    allowFullScreen
                    onLoad={() => handleMediaLoad(submissionId, 'presentation')}
                  />
                );
              } else {
                // Use Office Online for PowerPoint files
                return (
                  <iframe
                    src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
                      fileUrl
                    )}`}
                    className="w-full h-[50vh] min-h-[400px] max-h-[600px] border-0"
                    title="PowerPoint Presentation"
                    frameBorder="0"
                    allowFullScreen
                    onLoad={() => handleMediaLoad(submissionId, 'presentation')}
                    onError={() => {
                      console.log(
                        'Office Online failed, falling back to Google Docs'
                      );
                      // Fallback will be handled by the error state
                    }}
                  />
                );
              }
            })()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PresentationTab;
